import { useEffect, useState } from 'react';
import { categoryLabel } from '../../lib/categories';
import type { ReactNode } from 'react';

import type {
    CaseDetailDto,
    CaseMessageDto,
    CaseReviewDto,
    FollowupRequest,
    Verdict,
} from '../../api/types';
import { fmtDate, fmtDateTime } from '../../lib/format';
import { sourceLabel, turnTypeLabel } from '../../lib/stages';
import { HELP } from '../../lib/help';
import { HelpTip } from '../HelpTip';
import Checklist from './Checklist';
import CuratorChat from './CuratorChat';
import MessageBubble from './MessageBubble';

/** Как обращение попало в систему: колонка `gms_cases.source_kind` (`0002_mini_baseline.sql`). */
const SOURCE_KIND_LABELS: Record<string, string> = {
    text: 'текст',
    image: 'изображение',
    pdf: 'PDF',
    docx: 'DOCX',
};

function sourceKindLabel(kind: string): string {
    return SOURCE_KIND_LABELS[kind] ?? kind;
}

/** Цвет бейджа хода: базовый синий у квалификации, зелёный у фактуры, красный у чужого обращения. */
const TURN_BADGE_CLASS: Record<string, string> = {
    question: ' tb-question',
    facts: ' tb-facts',
    new_appeal: ' tb-new',
};

export interface TabThreadProps {
    detail: CaseDetailDto;
    /** ФИО текущего пользователя — подпись под ходом, который ещё отправляется. */
    userName: string | null;
    busy: boolean;
    pending: boolean;
    pendingTurn: { text: string; fileName: string | null } | null;
    onCheck: (checkId: string, done: boolean) => void;
    onReview: (verdict: Verdict, comment: string, messageId: number | null) => void;
    onUndo: (messageId: number) => void;
    onSplitOut: (messageId: number) => void;
    onReclassify: (category: string) => void;
    onSendTurn: (input: FollowupRequest, fileName: string | null) => Promise<void>;
    onNotice: (message: string) => void;
}

/**
 * Вкладка «Разбор обращения» (ТЗ §8.5.1): обращение заявителя, разбор ИИ с оценкой,
 * ходы куратора, чек-лист, правовое основание, документы и форма нового хода.
 */
export function TabThread({
    detail,
    userName,
    busy,
    pending,
    pendingTurn,
    onCheck,
    onReview,
    onUndo,
    onSplitOut,
    onReclassify,
    onSendTurn,
    onNotice,
}: TabThreadProps) {
    const { case: item, card, messages, permissions } = detail;

    const [reviewBox, setReviewBox] = useState<{ messageId: number | null; comment: string } | null>(null);
    const [reclassifyTo, setReclassifyTo] = useState('');
    const [bannerHidden, setBannerHidden] = useState(false);
    const [elapsed, setElapsed] = useState(0);

    // Ответ куратора идёт до минуты (с поиском по базе норм): показываем честный счётчик, а не спиннер.
    useEffect(() => {
        if (!pending) {
            setElapsed(0);
            return;
        }
        const started = Date.now();
        const timer = window.setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 1000);
        return () => window.clearInterval(timer);
    }, [pending]);

    const appeals = messages.filter((m) => m.turn_type === 'appeal');
    const initial = messages.find((m) => m.role === 'assistant' && m.turn_type === 'initial') ?? null;
    const turns = messages.filter((m) => m.turn_type !== 'appeal' && m !== initial);

    // Если мини не завела отдельный ход «квалификация», разбор берём из `summary_md`.
    const analysisText = initial ? initial.content : item.summary_md;
    const analysisId = initial ? initial.id : null;

    function reviewFor(messageId: number | null): CaseReviewDto | null {
        return detail.reviews.find((r) => (r.message_id ?? null) === (messageId ?? null)) ?? null;
    }

    function userTextBefore(message: CaseMessageDto): string | null {
        if (message.role === 'user') return message.content;
        const index = messages.indexOf(message);
        for (let i = index - 1; i >= 0; i -= 1) {
            if (messages[i].role === 'user') return messages[i].content;
        }
        return null;
    }

    function reviewRow(messageId: number | null, badge: string, badgeClass: string, extra?: ReactNode): ReactNode {
        const current = reviewFor(messageId);
        return (
            <div className="turn-meta">
                <span className={'turn-badge' + badgeClass}>{badge}</span>
                {extra}
                {permissions.review ? (
                    <span className="review-wrap">
                        <button
                            type="button"
                            className={current && current.verdict === 'ok' ? 'review-btn on-ok' : 'review-btn'}
                            disabled={busy}
                            onClick={() => {
                                setReviewBox(null);
                                onReview('ok', '', messageId);
                            }}
                        >
                            Замечаний нет
                        </button>
                        <button
                            type="button"
                            className={current && current.verdict === 'issues' ? 'review-btn on-issues' : 'review-btn'}
                            disabled={busy}
                            onClick={() => setReviewBox({ messageId, comment: current?.comment ?? '' })}
                        >
                            Есть замечания
                        </button>
                    </span>
                ) : null}
            </div>
        );
    }

    function reviewComment(messageId: number | null): ReactNode {
        if (!reviewBox || reviewBox.messageId !== messageId) return null;
        const submit = () => {
            onReview('issues', reviewBox.comment.trim(), messageId);
            setReviewBox(null);
        };
        return (
            <div className="comment-box">
                <input
                    type="text"
                    value={reviewBox.comment}
                    placeholder="Что не так в разборе"
                    aria-label="Замечания к разбору"
                    onChange={(event) => setReviewBox({ messageId, comment: event.target.value })}
                    onKeyDown={(event) => {
                        if (event.key !== 'Enter') return;
                        event.preventDefault();
                        submit();
                    }}
                />
                <button type="button" className="act-btn primary" disabled={busy} onClick={submit}>
                    Сохранить оценку
                </button>
                <button type="button" className="act-btn" onClick={() => setReviewBox(null)}>
                    Отмена
                </button>
            </div>
        );
    }

    const legalLines: string[] = [];
    if (card.legal_basis) {
        if (card.legal_basis.violated_right) legalLines.push(card.legal_basis.violated_right);
        card.legal_basis.citations.forEach((c) => legalLines.push('- ' + c.document_title + ', ' + c.article_label + (c.caveat ? ' (' + c.caveat + ')' : '')));
    }

    const reclassifyOptions = card.categories.filter((c) => c !== card.primary_category);
    const showBanner = card.reclassify_suggested && permissions.reclassify && !bannerHidden;
    const clock = Math.floor(elapsed / 60) + ':' + (elapsed % 60 < 10 ? '0' : '') + (elapsed % 60);

    return (
        <>
            {appeals.length === 0 && !analysisText && turns.length === 0 ? (
                <div className="detail-empty">По этому делу текст обращения не приложен.</div>
            ) : (
                <div className="thread">
                    {appeals.map((message) => (
                        <MessageBubble
                            key={'appeal-' + message.id}
                            variant="user"
                            from={
                                'Заявитель · ' +
                                (item.applicant ?? message.author_name) +
                                ' · ' +
                                fmtDate(message.created_at)
                            }
                            text={message.content}
                            attachments={message.attachments}
                        />
                    ))}

                    {analysisText ? (
                        <MessageBubble variant="bot" from="Ассистент по обращениям" markdown={analysisText}>
                            {reviewRow(analysisId, 'квалификация', '')}
                        </MessageBubble>
                    ) : null}
                    {analysisText ? reviewComment(analysisId) : null}

                    {turns.map((message) => {
                        if (message.role === 'user') {
                            return (
                                <MessageBubble
                                    key={'turn-' + message.id}
                                    variant="user"
                                    superseded={message.superseded}
                                    from={
                                        (message.author_name || userName || 'Исполнитель') +
                                        ' · ' +
                                        fmtDateTime(message.created_at)
                                    }
                                    text={message.content}
                                    attachments={message.attachments}
                                />
                            );
                        }

                        const badge = turnTypeLabel(message.turn_type) || 'ход';
                        const badgeClass = message.turn_type ? (TURN_BADGE_CLASS[message.turn_type] ?? '') : '';
                        const index = messages.indexOf(message);
                        const prev = index > 0 ? messages[index - 1] : null;
                        const isNewAppeal =
                            message.turn_type === 'new_appeal' ||
                            (prev !== null && prev.role === 'user' && prev.turn_type === 'new_appeal');
                        const factsText = userTextBefore(message);

                        const extra = (
                            <>
                                {isNewAppeal && permissions.split_out && message.id > 0 ? (
                                    <button
                                        type="button"
                                        className="review-btn"
                                        disabled={busy}
                                        onClick={() => onSplitOut(message.id)}
                                    >
                                        Завести как новое обращение
                                    </button>
                                ) : null}
                                {isNewAppeal && permissions.followup && factsText ? (
                                    <button
                                        type="button"
                                        className="review-btn"
                                        disabled={busy || pending}
                                        onClick={() => {
                                            // Ошибку уже показал тост — здесь только гасим отказ промиса.
                                            onSendTurn({ message: factsText, force_turn_type: 'facts' }, null).catch(
                                                () => undefined,
                                            );
                                        }}
                                    >
                                        Это фактура по текущему делу
                                    </button>
                                ) : null}
                                {!isNewAppeal && permissions.split_out && message.id > 0 ? (
                                    <span className="act-with-help">
                                        <button
                                            type="button"
                                            className="review-btn"
                                            disabled={busy}
                                            onClick={() => onSplitOut(message.id)}
                                        >
                                            ✂ Вынести в новое дело
                                        </button>
                                        <HelpTip text={HELP.splitOut} label="вынести в новое дело" />
                                    </span>
                                ) : null}
                                {permissions.undo && message.id > 0 && !message.superseded ? (
                                    <span className="act-with-help">
                                        <button
                                            type="button"
                                            className="review-btn"
                                            disabled={busy}
                                            onClick={() => onUndo(message.id)}
                                        >
                                            ↩ Откатить ход
                                        </button>
                                        <HelpTip text={HELP.undo} label="откатить ход" />
                                    </span>
                                ) : null}
                            </>
                        );

                        return (
                            <MessageBubble
                                key={'turn-' + message.id}
                                variant="bot"
                                superseded={message.superseded}
                                from="Куратор дела"
                                markdown={message.content}
                            >
                                {reviewRow(message.id, badge, badgeClass, extra)}
                                {reviewComment(message.id)}
                            </MessageBubble>
                        );
                    })}

                    {pendingTurn ? (
                        <MessageBubble
                            variant="user"
                            from={(userName ?? 'Вы') + ' · отправляется'}
                            text={pendingTurn.text}
                            attachments={
                                pendingTurn.fileName
                                    ? [{ name: pendingTurn.fileName, mime: '', size: 0, storage_key: '' }]
                                    : null
                            }
                        />
                    ) : null}

                    {pending ? (
                        <>
                            <div className="msg-from">Куратор дела</div>
                            <div className="ai-thinking" role="status" aria-live="polite">
                                <span className="ai-dots">
                                    <i />
                                    <i />
                                    <i />
                                </span>
                                {'Готовит ответ… ' +
                                    clock +
                                    '. Обычно это до минуты; окно можно закрыть — ответ появится в переписке.'}
                            </div>
                        </>
                    ) : null}
                </div>
            )}

            {showBanner ? (
                <div className="case-section">
                    <h4>⚠️ Куратор предлагает переквалифицировать дело</h4>
                    <div className="legal-box">
                        {card.reclassify_reason ?? 'Куратор не указал причину — сверьтесь с ходом переписки.'}
                    </div>
                    <div className="draft-bar">
                        {reclassifyOptions.length > 0 ? (
                            <select
                                className="act-select"
                                aria-label="Новая категория дела"
                                value={reclassifyTo}
                                onChange={(event) => setReclassifyTo(event.target.value)}
                            >
                                <option value="">Выберите категорию…</option>
                                {reclassifyOptions.map((category) => (
                                    <option key={category} value={category}>
                                        {categoryLabel(category)}
                                    </option>
                                ))}
                            </select>
                        ) : null}
                        <button
                            type="button"
                            className="act-btn primary"
                            disabled={busy || reclassifyTo === ''}
                            onClick={() => onReclassify(reclassifyTo)}
                        >
                            Переквалифицировать
                        </button>
                        <button type="button" className="act-btn" onClick={() => setBannerHidden(true)}>
                            Оставить как есть
                        </button>
                        {reclassifyOptions.length === 0 ? (
                            <span className="draft-note">
                                Куратор не назвал другую категорию из классификации — смените её вручную.
                            </span>
                        ) : null}
                    </div>
                </div>
            ) : null}

            <Checklist
                items={detail.checklist}
                progress={detail.checklist_progress}
                canCheck={permissions.checklist}
                busy={busy}
                onToggle={onCheck}
            />

            {legalLines.length > 0 ? (
                <div className="case-section">
                    <h4>⚖️ Правовое основание</h4>
                    <div className="legal-box">{legalLines.join('\n')}</div>
                </div>
            ) : null}

            {card.required_documents.length > 0 ? (
                <div className="case-section">
                    <h4>Какие документы нужны</h4>
                    <ul className="doc-list">
                        {card.required_documents.map((doc, index) => (
                            <li key={'doc-' + index}>{doc}</li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {detail.attachments.length > 0 ? (
                <div className="case-section">
                    <h4>Вложения</h4>
                    <ul className="doc-list">
                        {detail.attachments.map((file) => (
                            <li key={file.id}>
                                <a className="link-btn" href={file.url} download>
                                    {file.name}
                                </a>
                                {' · ' + (file.mime || 'файл')}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}

            <CuratorChat
                canFollowup={permissions.followup}
                pending={pending}
                busy={busy}
                onSend={onSendTurn}
                onNotice={onNotice}
            />

            {/* Сноска об источнике (ui-case-card.md §7.6): в рабочей системе вместо
                демо-дисклеймера макета — реальная атрибуция обращения. */}
            <div className="src-note">
                Источник обращения: {sourceLabel(item.source)}
                {item.source_kind ? ` · ${sourceKindLabel(item.source_kind)}` : ''}
                {'. Поступило: '}
                {fmtDateTime(item.received_at) || 'дата не указана'}
                {'. '}
                {item.received_at_provided
                    ? 'Сроки рассчитаны от даты поступления.'
                    : 'Дата поступления в источнике не указана — сроки рассчитаны от даты обработки.'}
            </div>
        </>
    );
}

export default TabThread;
